import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Card, Pill, LiveDot, Mono, Wave, Button } from "../components/v3";

/**
 * Home — primary operator surface.
 *
 * Layout: hero strip (live call OR "no calls right now") → metric row →
 * two-column (activity timeline + needs-eyes column).
 *
 * Data wires to /api/voice/sessions/stats (already shipped in W4) and
 * /api/voice/sessions?status=active|recent. Empty states are first-class —
 * a brand-new account should still feel built-out, not broken.
 */
export function Home() {
  return (
    <div className="px-8 py-6 max-w-[1280px] mx-auto">
      <PageHeader />

      <div className="mt-6 space-y-6">
        <LiveCallHero />
        <MetricRow />

        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
          <ActivityTimeline />
          <NeedsEyesColumn />
        </div>
      </div>
    </div>
  );
}

function PageHeader() {
  return (
    <div className="flex items-end justify-between">
      <div>
        <Mono className="block mb-2">Today · {formatToday()}</Mono>
        <h1 className="font-display text-4xl leading-none">
          What Vooice <span className="font-display-italic">did</span> today.
        </h1>
      </div>
      <div className="hidden md:flex gap-2">
        <Button variant="ghost" size="sm" as="a" href="/app/calls">
          All calls
        </Button>
        <Button variant="amber" size="sm" as="a" href="/app/calls/test">
          Call her
        </Button>
      </div>
    </div>
  );
}

function formatToday(): string {
  return new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

// ── Live call hero ─────────────────────────────────────────────────────────
// When a call is active, the hero shows it. Otherwise, a quieter "no calls"
// state with a hint about how Vooice will appear here when the next one starts.

function LiveCallHero() {
  // Use voice.sessions list filtered to active. Falls through to empty state.
  const { data, isLoading } = useQuery({
    queryKey: ["home", "live-call"],
    queryFn: async () => {
      const res = await fetch("/api/voice/sessions?status=active&limit=1", {
        credentials: "include",
      });
      if (!res.ok) return null;
      const json = await res.json();
      return (json.sessions ?? json.data ?? [])[0] ?? null;
    },
    refetchInterval: 5000,
  });

  if (isLoading) {
    return <Card padding="lg" className="h-[140px]"><div className="skeleton h-full" /></Card>;
  }

  if (!data) return <NoCallHero />;

  return (
    <Card padding="lg" glow className="border-amber/30">
      <div className="flex items-center justify-between gap-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <LiveDot />
            <Mono tone="amber">On the line now</Mono>
          </div>
          <div className="font-display text-2xl leading-tight">
            <span className="text-t2">Calling </span>
            <span className="text-t1 underline decoration-amber/40 underline-offset-4">
              {data.callee_number ?? data.caller_number ?? "unknown"}
            </span>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Mono tone="t2">
              {data.duration_seconds ?? 0}s elapsed
            </Mono>
            <Mono>·</Mono>
            <Mono tone="t2">{data.status}</Mono>
          </div>
        </div>

        <div className="flex flex-col items-end gap-3 shrink-0">
          <Wave bars={20} height={36} />
          <div className="flex gap-2">
            <Button as="a" href={`/app/calls/${data.id}`} variant="paper" size="sm">
              Open live
            </Button>
            <Button variant="ghost" size="sm">
              Bring me in
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function NoCallHero() {
  return (
    <Card padding="lg">
      <div className="flex items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="size-1.5 rounded-full bg-t4" />
            <Mono>Standby</Mono>
          </div>
          <div className="font-display text-2xl leading-tight text-t1">
            No calls right now.
          </div>
          <div className="text-t2 mt-2 max-w-[42ch]">
            Vooice will appear here when the next one starts —{" "}
            <span className="font-display-italic">incoming, outbound, or web</span>.
          </div>
        </div>
        <Button as="a" href="/app/calls/test" variant="amber" size="md">
          Test call
        </Button>
      </div>
    </Card>
  );
}

// ── Metric row ─────────────────────────────────────────────────────────────

function MetricRow() {
  const { data } = useQuery({
    queryKey: ["home", "stats"],
    queryFn: async () => {
      const res = await fetch("/api/voice/sessions/stats", {
        credentials: "include",
      });
      if (!res.ok) return null;
      return res.json();
    },
  });

  // Stats endpoint returns shape; if it errors out we fall back to dashes.
  const stats = data ?? {};
  const items = [
    { value: stats.total_today ?? "—", unit: "today", label: "Calls handled" },
    { value: stats.outbound_today ?? "—", unit: "out", label: "Outbound" },
    { value: stats.success_rate ?? "—", unit: "%", label: "Slot read-back pass" },
    { value: stats.needs_review ?? 0, unit: "pending", label: "Needs your eyes" },
  ];

  return (
    <Card padding="none" className="overflow-hidden">
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-line-soft">
        {items.map((it) => (
          <Metric key={it.label} {...it} />
        ))}
      </div>
    </Card>
  );
}

function Metric({
  value,
  unit,
  label,
}: {
  value: string | number;
  unit: string;
  label: string;
}) {
  return (
    <div className="px-6 py-5">
      <div className="flex items-baseline gap-2">
        <span className="font-display text-3xl leading-none">{value}</span>
        <Mono>{unit}</Mono>
      </div>
      <Mono className="block mt-2">{label}</Mono>
    </div>
  );
}

// ── Activity timeline ──────────────────────────────────────────────────────

interface ActivityItem {
  id: string;
  time: string;
  type: "inbound" | "outbound" | "email" | "message" | "reminder";
  who: string;
  body: string;
  action: string;
  href?: string;
  highlight?: boolean;
}

function ActivityTimeline() {
  const { data, isLoading } = useQuery<ActivityItem[]>({
    queryKey: ["home", "activity"],
    queryFn: async () => {
      // Real wiring: GET /api/voice/sessions?limit=12 (newest first)
      const res = await fetch("/api/voice/sessions?limit=12", {
        credentials: "include",
      });
      if (!res.ok) return [];
      const json = await res.json();
      const sessions: any[] = json.sessions ?? json.data ?? [];
      return sessions.map((s): ActivityItem => ({
        id: s.id,
        time: formatTime(s.started_at),
        type: s.direction === "outbound" ? "outbound" : "inbound",
        who: s.caller_number ?? s.callee_number ?? "Unknown",
        body: s.transcript_summary ?? s.task_outcome ?? `Call · ${s.status}`,
        action: actionFor(s),
        href: `/app/calls/${s.id}`,
      }));
    },
  });

  return (
    <Card padding="none" className="overflow-hidden flex flex-col min-h-[400px]">
      <div className="flex items-center justify-between px-5 py-4 border-b border-line-soft">
        <div>
          <Mono>Activity · {formatToday()}</Mono>
          <h2 className="font-display text-lg mt-1">What Vooice did</h2>
        </div>
        <Mono>Filter · All</Mono>
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="p-5 space-y-3">
            <div className="skeleton h-12" />
            <div className="skeleton h-12" />
            <div className="skeleton h-12" />
          </div>
        ) : data && data.length > 0 ? (
          data.map((item) => <ActivityRow key={item.id} item={item} />)
        ) : (
          <EmptyState
            title="Nothing yet today"
            body="When Vooice handles a call, it'll show up here."
          />
        )}
      </div>
    </Card>
  );
}

function ActivityRow({ item }: { item: ActivityItem }) {
  const dotColor = {
    inbound: "bg-plum",
    outbound: "bg-amber",
    email: "bg-t3",
    message: "bg-t3",
    reminder: "bg-rose",
  }[item.type];

  return (
    <Link
      to={item.href ?? "#"}
      className={[
        "block px-5 py-4 border-l-2 border-transparent transition-colors",
        "hover:bg-white/[0.02]",
        item.highlight && "border-l-amber bg-amber/[0.04]",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="grid grid-cols-[60px_8px_1fr_auto] gap-4 items-baseline">
        <Mono>{item.time}</Mono>
        <span className={`size-2 rounded-full ${dotColor} mt-1`} />
        <div>
          <div className="font-medium text-t1 truncate">{item.who}</div>
          <div className="text-t2 text-[13px] mt-1 line-clamp-2 leading-snug">
            {item.body}
          </div>
        </div>
        <Mono tone="t2">{item.action}</Mono>
      </div>
    </Link>
  );
}

function actionFor(s: any): string {
  if (s.status === "active" || s.status === "starting") return "Live";
  if (s.task_outcome === "success") return "Done";
  if (s.task_outcome === "failure") return "Failed";
  if (s.status === "ended") return "Ended";
  return s.status?.toUpperCase() ?? "—";
}

function formatTime(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "—";
  }
}

// ── "Needs your eyes" column ───────────────────────────────────────────────

function NeedsEyesColumn() {
  // Placeholder list — real wiring (drafts, pending approvals) lands in Phase 7
  // when Policy + signal-bus surfaces are integrated.
  const items: Array<{
    channel: string;
    head: string;
    body: string;
    meta: string;
  }> = [];

  return (
    <Card padding="none" className="overflow-hidden flex flex-col min-h-[400px]">
      <div className="px-5 py-4 border-b border-line-soft">
        <Mono>Needs your eyes</Mono>
        <h2 className="font-display text-lg mt-1">
          {items.length === 0 ? "Nothing pending" : `${items.length} to review`}
        </h2>
      </div>
      <div className="flex-1 overflow-auto">
        {items.length === 0 ? (
          <EmptyState
            title="Inbox zero"
            body="Drafts and confirmations Vooice wants you to look at will land here."
          />
        ) : (
          items.map((it, i) => <NeedsRow key={i} {...it} />)
        )}
      </div>
    </Card>
  );
}

function NeedsRow({
  channel,
  head,
  body,
  meta,
}: {
  channel: string;
  head: string;
  body: string;
  meta: string;
}) {
  return (
    <div className="px-5 py-4 border-b border-line-soft last:border-b-0">
      <div className="flex items-center justify-between mb-2">
        <Mono tone="amber">{channel}</Mono>
        <Pill tone="warn">Waiting</Pill>
      </div>
      <div className="font-medium text-t1">{head}</div>
      <div className="mt-2 pl-3 border-l-2 border-line-soft text-[13px] text-t2 italic font-display leading-snug">
        {body}
      </div>
      <div className="flex items-center justify-between mt-3">
        <Mono>{meta}</Mono>
        <Button variant="paper" size="sm">
          Review
        </Button>
      </div>
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex flex-col items-center justify-center text-center px-8 py-16 gap-2">
      <div className="font-display text-xl text-t2">{title}</div>
      <div className="text-t3 text-sm max-w-[36ch]">{body}</div>
    </div>
  );
}
